// System console: email delivery (PB-13-04; email stream PD-580 – PD-587; owner decision B-02). The
// installation sender and SMTP connection, a test email, and per organisation whether it may send
// as itself (its own administrators then configure it in Communications).
import type { EmailTestResult, InstallationEmailSettingsResponse, PlatformOrganisationEmailSummary } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiRequestError, api, get } from '../api.ts';
import { SenderForm, TestEmailPanel } from '../components/email-ui.tsx';
import { ErrorState, ErrorSummary, Field, Loading, PageHead } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';

const P = '/api/platform/v1';

export function PlatformEmailPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ['platform', 'email-settings'];
  const q = useQuery({ queryKey: key, queryFn: () => get<InstallationEmailSettingsResponse>(`${P}/email-settings`) });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  return (
    <>
      <PageHead eyebrow={t('platform.eyebrow')} title={t('eml.platform.title')} intro={t('eml.platform.intro')} />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : (
        <>
          <section className="panel">
            <div className="pad">
              <SenderForm key={q.data.version} scope="installation" initial={q.data} save={(body) => api('PUT', `${P}/email-settings`, body)} onSaved={refresh} />
              <p className="tiny">{t('eml.platform.secretKeys')}</p>
            </div>
          </section>
          <TestEmailPanel send={(to) => api<EmailTestResult>('POST', `${P}/email-settings/test`, { to })} lastTest={q.data.lastTest} onDone={refresh} />
        </>
      )}
    </>
  );
}

/** Organisation page panel: allow or withdraw the organisation's own sender (PD-581). */
export function PlatformTenantEmailPanel({ tenantId }: { tenantId: string }) {
  const { t, tk, dateTime } = useI18n();
  const qc = useQueryClient();
  const key = ['platform', 'tenant', tenantId, 'email'];
  const q = useQuery({ queryKey: key, queryFn: () => get<PlatformOrganisationEmailSummary>(`${P}/tenants/${tenantId}/email-settings`) });
  const [reason, setReason] = useState('');
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const m = useMutation({
    mutationFn: (value: boolean) => api<PlatformOrganisationEmailSummary>('PUT', `${P}/tenants/${tenantId}/email-settings/allowed`, { allowed: value, reason }),
    onSuccess: () => {
      setReason('');
      setAllowed(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const fe = m.error instanceof ApiRequestError ? m.error.fields : {};
  const d = q.data;
  const value = allowed ?? d?.allowed ?? false;
  return (
    <section className="panel" aria-labelledby="pem-title">
      <div className="panel-head">
        <div>
          <h2 id="pem-title">{t('eml.platform.org.title')}</h2>
          <p>{t('eml.platform.org.sub')}</p>
        </div>
        {d ? <span className={`badge ${d.inEffect === 'organisation' ? 'green' : ''}`}>{t(d.inEffect === 'organisation' ? 'eml.inEffect.organisation' : 'eml.inEffect.installation')}</span> : null}
      </div>
      <div className="pad stack">
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} />
        ) : (
          <>
            {d!.override && (d!.override.fromAddress || d!.override.sendingDomain) ? (
              <p className="tiny" data-ltr>
                {[d!.override.sendingDomain, d!.override.fromAddress, d!.override.smtpHost].filter(Boolean).join(' · ')}
                {' · '}
                {t(d!.override.verifiedCurrent ? 'eml.org.verified' : 'eml.org.notVerified')}
                {d!.override.lastTest.at && d!.override.lastTest.outcome ? ` · ${t('eml.lastTest', { at: dateTime(d!.override.lastTest.at), outcome: tk(`eml.test.outcome.${d!.override.lastTest.outcome}`) })}` : ''}
              </p>
            ) : (
              <p className="tiny">{t('eml.platform.org.notConfigured')}</p>
            )}
            <form
              noValidate
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                m.mutate(value);
              }}
            >
              <ErrorSummary error={m.error} fieldIds={{ reason: 'pem-reason' }} />
              <div className="formgrid">
                <div className="field full">
                  <label>
                    <input type="checkbox" checked={value} onChange={(e) => setAllowed(e.target.checked)} /> {t('eml.platform.org.allow')}
                  </label>
                </div>
                <Field id="pem-reason" label={t('eml.platform.org.reason')} error={fe.reason} full>
                  {(p) => <input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
                </Field>
              </div>
              <div className="form-actions">
                <button className="button" type="submit" disabled={m.isPending || value === d!.allowed}>
                  {t('eml.platform.org.apply')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}
